import { Descriptions, Flex, Modal, Space, Tag, Timeline, Typography } from 'antd'
import {
  CheckOutlined,
  CloseOutlined,
  StopOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import {
  useCommentList,
  useCommentsGate,
  useCurrencies,
  useCurrencyGate,
  useModalRequestView,
} from 'src/pages/budget/budgetRequests/hooks'
import {
  closeModalRequestView,
  openModalRequestComment,
} from 'src/pages/budget/budgetRequests/model'
import { LinkifiedText, UiText } from 'src/pages/budget/components'
import {
  FREQUENCY_OPTIONS,
  STATUS,
  STATUS_MAP_COLOR,
  STATUS_MAP_NAME,
} from 'src/pages/budget/const'
import { ButtonContentByStatus } from './ButtonContent'
import { CostDescription } from './CostDescription'
import {
  BudgetRequest,
  Currency,
} from 'src/pages/budget/budgetRequests/types'
import {
  formatAmount,
  formatUsdAmount,
  getUsdEquivalent,
} from 'src/pages/budget/budgetRequests/currency'
import { CommentList } from './CommentList'
import { TStatusPeriod } from 'src/pages/budget/types'

type ModalRequestViewContentProps = {
  request: BudgetRequest
  mode: 'own' | 'forApprove'
  periodStatus?: TStatusPeriod
  currencies: Currency[]
}

export const ModalRequestView = () => {
  useCurrencyGate()
  const currencies = useCurrencies()
  const modalRequestView = useModalRequestView()

  if (!modalRequestView) return null

  const { items, mode, periodStatus } = modalRequestView

  return (
    <ModalRequestViewContent
      request={items[0]}
      mode={mode}
      periodStatus={periodStatus}
      currencies={currencies}
    />
  )
}

const ModalRequestViewContent = ({
  request,
  periodStatus,
  mode,
  currencies,
}: ModalRequestViewContentProps) => {
  const {
    id,
    article,
    articleItem,
    status,
    productIds,
    createdBy,
    description,
    amount,
    currency,
    costByCountryEnabled,
    costByCountry,
    autoPaymentEnabled,
    autoPaymentFrequency,
    isEarlyPayment,
    isInPlan,
    comment,
    approvalHistory = [],
    approvalQueue = [],
  } = request
  const usdEquivalent = getUsdEquivalent(
    amount,
    currency,
    currencies,
    request.approvedRate,
  )

  useCommentsGate(id)
  const commentList = useCommentList()

  const isOwn = mode === 'own'
  const canAddComment = [
    STATUS.SUBMITTED,
    STATUS.IN_REVIEW,
    STATUS.APPROVED,
    STATUS.REJECTED,
  ].includes(status)

  const addComment = () => {
    openModalRequestComment({ id, description })
    closeModalRequestView()
  }

  const { Item } = Descriptions
  const { Paragraph, Text } = Typography

  const getTextPaymentFrequency = (value: string) => {
    return (
      FREQUENCY_OPTIONS.find(
        (item) => item.value === value,
      )?.label.toLowerCase() ?? ''
    )
  }

  return (
    <Modal
      title={
        <Flex gap={16} align="center">
          <UiText size={32}>Заявка № {id}</UiText>

          <Tag color={STATUS_MAP_COLOR[status] ?? ''}>
            {mode === 'forApprove' && status === STATUS.SUBMITTED
              ? 'Ждет согласования'
              : STATUS_MAP_NAME[status] ?? status}
          </Tag>

          {isEarlyPayment ? (
            <Tag color="orange-inverse">Досрочная оплата</Tag>
          ) : (
            <Tag>Стандартная</Tag>
          )}

          {isInPlan === false && (
            <Tag color="volcano-inverse">Вне согласованного бюджета</Tag>
          )}
        </Flex>
      }
      centered
      wrapClassName="ant_modal"
      width={800}
      maskClosable
      open
      destroyOnHidden
      onCancel={() => closeModalRequestView()}
      footer={null}
    >
      {comment && (status === STATUS.DRAFT || status === STATUS.REJECTED) && (
        <Flex
          align="flex-start"
          vertical
          style={{
            border: '1px solid var(--crm-border)',
            backgroundColor: 'var(--color-red-10)',
            borderRadius: 5,
            padding: 8,
            marginTop: 5,
            marginBottom: 16,
          }}
        >
          {status === STATUS.REJECTED && (
            <Text
              type="secondary"
              style={{ fontSize: 18, whiteSpace: 'nowrap' }}
            >
              Причина отклонения
            </Text>
          )}
          {comment}
        </Flex>
      )}
      <Descriptions
        column={2}
        colon={false}
        layout="vertical"
        style={{ marginBottom: 16 }}
        styles={{
          label: { fontSize: 18, marginBottom: -16 },
          content: { fontSize: 18, alignItems: 'center' },
        }}
      >
        <Item label="Статья">
          <Paragraph
            ellipsis={{
              rows: 3,
              expandable: 'collapsible',
            }}
            style={{ fontSize: 18 }}
          >
            {article.id}.{article.name}
          </Paragraph>
        </Item>

        <Item label="Пункт">
          <Paragraph
            ellipsis={{
              rows: 3,
              expandable: 'collapsible',
            }}
            style={{ fontSize: 18 }}
          >
            {articleItem.fullNumber} {articleItem.name}
          </Paragraph>
        </Item>

        <Item label="Продукт">{productIds ? productIds.join(', ') : '-'}</Item>

        <Item label="Заявитель">{createdBy?.fullname ?? ''}</Item>

        <Item label="Описание заявки" span={2}>
          <Paragraph style={{ fontSize: 18 }}>
            <LinkifiedText text={description} />
          </Paragraph>
        </Item>

        <Item label="Стоимость" span={2}>
          <Flex vertical gap={0}>
            <span>
              {formatAmount(amount, currency)} {currency}
            </span>
            {usdEquivalent && (
              <span>
                {formatUsdAmount(usdEquivalent)}{' '}
                <Typography.Text type="secondary">USD</Typography.Text>
              </span>
            )}
          </Flex>
        </Item>
      </Descriptions>
      {costByCountryEnabled && (
        <CostDescription
          items={(costByCountry ?? []).map((item) => ({
            country: item.countryCode,
            amountCountry: `${formatAmount(item.amount, item.currency)} ${item.currency}`,
          }))}
          total={`${formatAmount(amount, 'USD')} USD`}
        />
      )}
      {approvalQueue.length > 0 && (
        <>
          <UiText size={18} strong>
            Очередь согласования
          </UiText>
          <Timeline
            style={{ marginTop: 12 }}
            items={approvalQueue.map((item) => {
              const status = {
                pending: { label: 'Ожидает', color: 'blue' },
                approved: { label: 'Согласовано', color: 'green' },
                rejected: { label: 'Отклонено', color: 'red' },
                skipped: { label: 'Пропущено', color: 'default' },
              }[item.status]
              const approverStatus = {
                pending: 'Ожидает',
                approved: 'Согласовал',
                rejected: 'Отклонил',
                skipped: 'Пропущен',
                not_required: 'Ещё не согласовал',
              }

              return {
                color: status.color,
                children: (
                  <Flex vertical gap={4}>
                    <Flex gap={8} align="center">
                      <UiText size={16} strong>
                        {item.level}. {item.teamName ?? 'Команда не указана'}
                      </UiText>
                      <Tag color={status.color}>{status.label}</Tag>
                    </Flex>
                    <UiText size={14} type="secondary">
                      Согласующие:{' '}
                      {item.approvers.length > 0
                        ? item.approvers
                            .map(
                              (approver) =>
                                `${approver.name} — ${approverStatus[approver.status]}`,
                            )
                            .join(', ')
                        : 'не назначены'}
                    </UiText>
                    {item.approvedBy && (
                      <UiText size={14} type="secondary">
                        Решение: {item.approvedBy}
                        {item.approvedAt
                          ? ` — ${new Date(item.approvedAt).toLocaleString('ru-RU')}`
                          : ''}
                      </UiText>
                    )}
                  </Flex>
                ),
              }
            })}
          />
        </>
      )}
      {approvalHistory.length > 0 && (
        <>
          <UiText size={18} strong>История согласования</UiText>
          <Timeline
            style={{ marginTop: 12 }}
            items={approvalHistory.map((item) => ({
              children: `${item.approvedBy} согласовал${item.teamName ? ` (${item.teamName})` : ''} — ${new Date(item.approvedAt).toLocaleString('ru-RU')}`,
            }))}
          />
        </>
      )}
      <CommentList
        list={commentList}
        onAddComment={canAddComment ? addComment : undefined}
      />
      <Space direction="vertical" style={{ marginTop: 16, marginBottom: 16 }}>
        {autoPaymentEnabled ? (
          <Flex>
            <CheckOutlined
              style={{
                marginRight: 8,
                color: 'var(--color-primary)',
              }}
            />

            <UiText size={18}>Заявка автоматически создается ежемесячно</UiText>
          </Flex>
        ) : (
          <Flex>
            <CloseOutlined
              style={{
                marginRight: 8,
                color: 'var(--crm-accent-red)',
              }}
            />

            <UiText size={18}>
              Автоматическое создание заявки каждый месяц отключено
            </UiText>
          </Flex>
        )}

        {autoPaymentFrequency ? (
          <Flex>
            <SyncOutlined
              style={{
                marginRight: 8,
                color: 'var(--color-primary)',
              }}
            />

            <UiText size={18}>
              Подключен автоплатеж{' '}
              {getTextPaymentFrequency(autoPaymentFrequency)}
            </UiText>
          </Flex>
        ) : (
          <Flex>
            <StopOutlined
              style={{
                marginRight: 8,
                color: 'var(--crm-accent-red)',
              }}
            />

            <UiText size={18}>Автоплатеж не подключен</UiText>
          </Flex>
        )}
      </Space>

      <ButtonContentByStatus
        isOwn={isOwn}
        requestStatus={status}
        request={request}
        periodStatus={periodStatus}
      />
    </Modal>
  )
}
